import React, { useEffect } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import { Modal } from "@mapmycustomers/ui";

import { getFrequencyModalAnalyticsPath, getFrequencyModalConfig } from "@app/store/frequency";
import { hideFrequencyModal, initFrequencyModal } from "@app/store/frequency/actions";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import layout from "@app/styles/layout";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";

import EntityBlock from "../EntityBlock";
import FrequencyEntity from "../type/FrequencyEntity";

import Activities from "./component/Activities";
import Frequencies from "./component/Frequencies";
import styles from "./FrequencyModal.module.scss";

interface Props {
  analyticsPath?: string[];
  entity: FrequencyEntity;
  onHideFrequencyModal: typeof hideFrequencyModal;
  onInitFrequencyModal: typeof initFrequencyModal.request;
  subscribe: typeof subscribe;
  unsubscribe: typeof unsubscribe;
}

const FrequencyModal = ({
  analyticsPath,
  entity,
  onHideFrequencyModal,
  onInitFrequencyModal,
  subscribe,
  unsubscribe,
}: Props) => {
  const intl = useIntl();

  const analytics = useExtendedAnalytics([...(analyticsPath ?? []), "Manage Frequencies"]);

  useEffect(() => {
    analytics.clicked([]);
  }, [analytics]);

  useEffect(() => {
    const listener = {
      onUpdate: () => onInitFrequencyModal({ entity }),
    };

    subscribe({ entityTypes: [entity.entity], listener });

    return () => {
      unsubscribe({ entityTypes: [entity.entity], listener });
    };
  }, [entity, onInitFrequencyModal, subscribe, unsubscribe]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      <Modal
        className={styles.container}
        footer={null}
        onCancel={() => onHideFrequencyModal()}
        title={
          <Row gutter={layout.spacerXS}>
            <Col>
              {intl.formatMessage({
                id: "frequency.frequencyModal.title",
                defaultMessage: "Manage Frequencies",
                description: "frequency component - frequency modal - title",
              })}
            </Col>
            <Col>
              <EntityBlock entity={entity} />
            </Col>
          </Row>
        }
        visible
        width="1024px"
      >
        <Row gutter={layout.spacerM}>
          <Col span={14}>
            <Activities />
          </Col>
          <Col span={10}>
            <Frequencies />
          </Col>
        </Row>
      </Modal>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  analyticsPath: getFrequencyModalAnalyticsPath(state),
  entity: getFrequencyModalConfig(state).entity,
});

const mapDispatchToProps = {
  onHideFrequencyModal: hideFrequencyModal,
  onInitFrequencyModal: initFrequencyModal.request,
  subscribe,
  unsubscribe,
};

export default connect(mapStateToProps, mapDispatchToProps)(FrequencyModal);
